import { useTranslations } from '@vocab/react';

import type { Site, SuggestionType } from 'src/shared/types';
import { brandLookup, type Brand } from 'src/utils/brandLookup';

import {
  INTEGRATION_SUGGESTION,
  MFA_SUGGESTION,
  TEMPLATE_SUGGESTION,
} from '../Suggestions/data';

import translations from './.vocab';

export interface SubItem {
  type: string;
  name: string;
  label: string;
  required: boolean;
  options?: Array<{
    name: string;
    label: string;
    value?: string;
  }>;
}

export interface Reason {
  reason: string;
  rspToggleRequired?: boolean;
  brand?: Brand[];
  suggestion?: SuggestionType;
  subItems?: SubItem[];
}

export type Subject = Record<string, Reason[]>;

export const useSubjects = (site: Site) => {
  const { t } = useTranslations(translations);
  const isANZ = site === 'employer-seek-au' || site === 'employer-seek-nz';
  const brand: Brand = brandLookup(site);

  const subjectData: Subject = {
    ...(brand !== 'SEEK' && {
      [t('Products and pricing')]: [
        { reason: t('Job Advertisements') },
        { reason: t('Talent Search') },
        { reason: t('Branding opportunity') },
        { reason: t('Other') },
      ],
    }),
    ...(brand !== 'SEEK' && {
      [t('Talent Sourcing Advice')]: [
        { reason: t('Need help hiring') },
        { reason: t('Bulk hiring') },
        { reason: t('Urgent hiring') },
        { reason: t('Proactive sourcing with Talent Search') },
      ],
    }),
    [isANZ ? t('Posting an Ad') : t('Sales-Related Inquiries')]: [
      { reason: t('Register for an advertiser account') },
      { reason: t('How to post an Ad') },
      { reason: t('Products & pricing information'), brand: ['SEEK'] },
      { reason: t('Ad writing support') },
      {
        reason: t('How to boost my job ad performance'),
        brand: ['Jobsdb', 'Jobstreet'],
      },
      {
        reason: t('How to attract the right talent'),
        brand: ['Jobsdb', 'Jobstreet'],
      },
      { reason: t('Delete account'), brand: ['SEEK'] },
      { reason: t('Other') },
    ],
    ...(isANZ && {
      [t('My Advertiser Account')]: [
        {
          reason: t('Job ad templates'),
          suggestion: TEMPLATE_SUGGESTION,
          brand: ['SEEK'],
        },
        { reason: t('My log in details'), suggestion: MFA_SUGGESTION },
        { reason: t('Add / delete users') },
        { reason: t('Update my company details') },
        { reason: t('Update account details'), brand: ['SEEK'] },
        { reason: t('Update primary contact information'), brand: ['SEEK'] },
        { reason: t('Update my company name'), brand: ['SEEK'] },
        { reason: t('Advertising privately'), brand: ['SEEK'] },
        { reason: t('Resend activation email') },
        { reason: t('Password reset') },
        { reason: t('Other') },
      ],
    }),
    ...(isANZ && {
      [t('Managing Ads')]: [
        {
          reason: t('Where are my custom templates?'),
          suggestion: TEMPLATE_SUGGESTION,
          brand: ['SEEK'],
        },
        {
          reason: t('Edit, copy, refresh or expire my live ads'),
          brand: ['SEEK'],
        },
        { reason: t('My ad’s performance'), brand: ['SEEK'] },
        { reason: t('SEEK Talent Search') },
        { reason: t('Candidate management'), brand: ['SEEK'] },
        { reason: t('Application notification preferences'), brand: ['SEEK'] },
        { reason: t('Job ad location'), brand: ['SEEK'] },
        { reason: t('Job ad classification'), brand: ['SEEK'] },
        { reason: t('Where is my ad?'), brand: ['SEEK'] },
        { reason: t('Not suitable emails, how to opt out?'), brand: ['SEEK'] },
        { reason: t('How to respond to candidates?'), brand: ['SEEK'] },
        { reason: t('Bulk export applications?'), brand: ['SEEK'] },
        {
          reason: t('Proof of advertising for immigration purposes'),
          brand: ['SEEK'],
        },
        { reason: t('Other') },
      ],
    }),
    ...(isANZ && {
      [t('Payment and Invoicing')]: [
        { reason: t('I want to pay my invoice') },
        { reason: t('I need an invoice re-sent') },
        { reason: t('Billing or invoice inquiry') },
        { reason: t('Terms of payment') },
        { reason: t('How to view invoice online'), brand: ['SEEK'] },
        { reason: t('Other') },
      ],
    }),
    ...(brand === 'SEEK' && {
      [t('Make a purchase')]: [
        { reason: t('Purchase Premium Talent Search') },
        { reason: t('Purchase a larger SEEK ad budget') },
      ],
    }),
    [t('Technical Issues')]: [
      { reason: t('Login error/issue'), suggestion: MFA_SUGGESTION }, // change language used
      { reason: t('Job posting error') },
      { reason: t('Talent Search error') },
      { reason: t('Not receiving application notifications') },
      { reason: t('Website/system is down') },
      { reason: t('Mobile app issue') },
      { reason: t('Other') },
    ],
    ...(brand === 'SEEK'
      ? {
          'Recruitment software providers': [
            { reason: 'RSP recommendation' },
            { reason: 'RSP integration status' },
            {
              reason: 'Integration request',
              suggestion: INTEGRATION_SUGGESTION,
            },
            {
              reason: 'I have technical issues',
              rspToggleRequired: true,
              subItems: [
                {
                  type: 'checkboxGroup',
                  name: 'rspRelatedProducts',
                  label: 'Which product does it relate to',
                  required: true,
                  options: [
                    {
                      name: 'rspJobPosting',
                      label: 'Job posting',
                      value: 'Job posting',
                    },
                    {
                      name: 'rspSeekOptimisedApply',
                      label: 'SEEK Optimised Apply',
                      value: 'SEEK Optimised Apply',
                    },
                    {
                      name: 'rspApplyWithSeek',
                      label: 'Apply with SEEK',
                      value: 'Apply with SEEK',
                    },
                    {
                      name: 'rspOther',
                      label: 'Other',
                      value: 'Other',
                    },
                  ],
                },
              ],
            },
            {
              reason: 'I have a query regarding SEEK products',
              rspToggleRequired: true,
            },
            {
              reason: 'Suspected security issue',
              rspToggleRequired: true,
              subItems: [
                {
                  type: 'checkboxGroup',
                  name: 'rspRelatedProducts',
                  label: 'Which product does it relate to',
                  required: true,
                  options: [
                    {
                      name: 'rspJobPosting',
                      label: 'Job posting',
                      value: 'Job posting',
                    },
                    {
                      name: 'rspSeekOptimisedApply',
                      label: 'SEEK Optimised Apply',
                      value: 'SEEK Optimised Apply',
                    },
                    {
                      name: 'rspApplyWithSeek',
                      label: 'Apply with SEEK',
                      value: 'Apply with SEEK',
                    },
                    {
                      name: 'rspPartnerSystem',
                      label: 'Partner system',
                      value: 'Partner system',
                    },
                    {
                      name: 'rspOther',
                      label: 'Other',
                      value: 'Other',
                    },
                  ],
                },
                {
                  type: 'checkboxGroup',
                  label: 'Action taken so far',
                  name: 'rspActions',
                  required: true,
                  options: [
                    {
                      name: 'rspIssueIsolated',
                      label: 'Issue isolated in Partner system',
                      value: 'Issue isolated in Partner system',
                    },
                    {
                      name: 'rspCustomersContacted',
                      label: 'Customers contacted',
                      value: 'Customers contacted',
                    },
                    {
                      name: 'rspIssueResolved',
                      label: 'Issue resolved, just notifying SEEK',
                      value: 'Issue resolved, just notifying SEEK',
                    },
                    {
                      name: 'rspNothing',
                      label: 'Nothing',
                      value: 'Nothing',
                    },
                  ],
                },
                {
                  type: 'inputPhone',
                  name: 'rspPhone',
                  required: true,
                  label: 'Phone number',
                },
                {
                  type: 'inputEmail',
                  name: 'rspEmail',
                  required: true,
                  label: 'Email',
                },
                {
                  type: 'inputName',
                  name: 'rspName',
                  required: true,
                  label: 'Name',
                },
              ],
            },
            { reason: 'Other' },
          ],
        }
      : {}),
    [isANZ ? t('Other') : t('Service Request')]: [
      { reason: t('General feedback') },
      { reason: t('Product feedback') },
      { reason: t('Company Reviews') },
    ],
    ...(!isANZ && {
      // If site is in APAC, Payment & Invoicing will be shown at last place
      [t('Payment and Invoicing')]: [
        { reason: t('I want to pay my invoice') },
        { reason: t('I need an invoice re-sent') },
        { reason: t('Billing or invoice inquiry') },
        { reason: t('Terms of payment') },
        { reason: t('How to view invoice online'), brand: ['SEEK'] },
        { reason: t('Other') },
      ],
    }),
  };

  return { subjectData };
};
